// Spools-tab action: split the job's nominal labour budget (Details
// tab → Nominal Labour Times) evenly across the job's spools into the
// existing per-spool stage hours.
//
// The current form values are sent with the request rather than read
// from the job row server-side, so the estimator can type a budget and
// distribute it without saving first — the server stores the budget on
// the job as part of the same transaction, so the refetch that follows
// brings back exactly what was distributed. Spool rows already marked
// complete keep their recorded hours.
import { useMemo, useState } from "react";
import { WELD_NOMINAL_KEY } from "../../../../../../shared/types/production-schedule";
import { useActiveStages } from "../components/StagesProvider";
import { distributeNominalHours } from "../hooks/useProductionApi";
import { budgetedRows, nominalRows, nominalTotal } from "../lib/nominalHours";

interface Props {
  jobId: number;
  // Persisted spools — the server splits across exactly these, so any
  // difference between the saved list and the draft list is called out
  // in the confirm rather than silently changing the shares.
  spoolCount: number;
  // Drafts with no id yet: they get no share until the job is saved.
  unsavedSpoolCount?: number;
  // Saved spools dropped from the draft list: still on the server, so
  // they take a share now and the remaining spools end up short once
  // the removal is saved.
  removedSpoolCount?: number;
  nominalStageHours: Record<string, number>;
  // Refetch hook — the job payload owns the stage rows the split just
  // rewrote, so the caller re-reads it after a successful run.
  onDistributed?: () => void;
}

export function DistributeNominalButton({
  jobId,
  spoolCount,
  unsavedSpoolCount = 0,
  removedSpoolCount = 0,
  nominalStageHours,
  onDistributed,
}: Props) {
  const catalogue = useActiveStages();
  const [busy, setBusy] = useState(false);
  const rows = useMemo(() => nominalRows(catalogue), [catalogue]);
  const budgeted = useMemo(
    () => budgetedRows(rows, nominalStageHours),
    [rows, nominalStageHours],
  );
  const total = nominalTotal(nominalStageHours);
  const disabled = busy || spoolCount === 0 || budgeted.length === 0;

  const title = spoolCount === 0
    ? "Add spools before distributing nominal hours"
    : budgeted.length === 0
      ? "Enter nominal labour times on the Details tab first"
      : `Split ${total.toFixed(1)}h of nominal labour evenly across ${spoolCount} spool${spoolCount === 1 ? "" : "s"}`;

  const run = async () => {
    if (disabled) return;
    const lines = budgeted
      .map((b) => `  ${b.row.label} — ${b.hours.toFixed(1)}h → ${(b.hours / spoolCount).toFixed(2)}h each`)
      .join("\n");
    // The split runs against the SAVED spool list, so spell out any
    // drift from the draft: unsaved additions miss out, and pending
    // removals still take a share, which leaves every other spool
    // short once the removal is saved.
    const drift: string[] = [];
    if (unsavedSpoolCount > 0) {
      drift.push(
        `${unsavedSpoolCount} unsaved spool${unsavedSpoolCount === 1 ? "" : "s"} `
        + `won't get a share.`,
      );
    }
    if (removedSpoolCount > 0) {
      drift.push(
        `${removedSpoolCount} spool${removedSpoolCount === 1 ? "" : "s"} you removed `
        + `${removedSpoolCount === 1 ? "is" : "are"} still saved, so `
        + `${removedSpoolCount === 1 ? "it takes" : "they take"} a share.`,
      );
    }
    const driftNote = drift.length > 0
      ? `\n\n${drift.join(" ")} Save the job and distribute again for exact shares.`
      : "";
    if (!window.confirm(
      `Distribute ${total.toFixed(1)}h of nominal labour across ${spoolCount} saved spool${spoolCount === 1 ? "" : "s"}?\n\n${lines}\n\n`
      + "This saves the nominal times on the job and overwrites each spool's stage hours "
      + "for the stages listed. Stages already marked complete keep their recorded hours, "
      + "and spools with welding cleared get no welding hours."
      + driftNote,
    )) return;
    setBusy(true);
    try {
      const res = await distributeNominalHours(jobId, nominalStageHours);
      const notes: string[] = [];
      if (res.skippedComplete > 0) {
        notes.push(`${res.skippedComplete} completed stage row${res.skippedComplete === 1 ? "" : "s"} left untouched.`);
      }
      if (res.ignoredKeys.length > 0) {
        const named = res.ignoredKeys.map((k) => (
          k === WELD_NOMINAL_KEY
            ? "Welding"
            : rows.find((r) => r.key === k)?.label ?? k
        ));
        notes.push(`Ignored (stage archived or removed): ${named.join(", ")}.`);
      }
      window.alert(
        `Distributed ${res.stages} stage${res.stages === 1 ? "" : "s"} across ${res.spools} spool${res.spools === 1 ? "" : "s"} — ${res.writes} stage row${res.writes === 1 ? "" : "s"} updated.`
        + (notes.length ? `\n\n${notes.join("\n")}` : ""),
      );
      onDistributed?.();
    } catch (e) {
      window.alert(`Distribute nominal hours failed: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      className="btn-sm"
      onClick={() => void run()}
      disabled={disabled}
      title={title}
      style={{
        background: disabled ? undefined : "var(--bsp-blue-tint)",
        borderColor: disabled ? undefined : "var(--bsp-blue)",
        color: disabled ? undefined : "var(--bsp-blue)",
      }}
    >
      {busy ? "Distributing…" : "⏱ Distribute nominal hrs"}
    </button>
  );
}
